import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { API } from "../constant/api";
import useAuthAxios from "../utils/baseAxios";
import { errorMessage } from "../utils/errorMessage";

export type Manager = {
  _id: string;
  businessId: string;
  userId: {
    _id: string;
    email?: { address?: string } | string;
    profile?: {
      firstName?: string;
      lastName?: string;
    };
  };
  invitedBy: string;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export type PendingInvite = {
  _id: string;
  businessId: string;
  invitedEmail: string;
  invitedBy: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
};

type ManagersPayload = {
  managers: Manager[];
  pendingInvites: PendingInvite[];
};

const managersKey = (businessId: string | undefined) => [
  "get-managers",
  businessId,
];

export const useManagers = (businessId: string | undefined) => {
  const axiosInstance = useAuthAxios();

  const query = useQuery({
    queryKey: managersKey(businessId),
    queryFn: async (): Promise<ManagersPayload> => {
      const { data } = await axiosInstance.get(
        API.getManagers(businessId!)
      );
      const payload = data?.data;
      return {
        managers: payload?.managers ?? [],
        pendingInvites: payload?.pendingInvites ?? [],
      };
    },
    enabled: !!businessId,
    staleTime: 2 * 60_000,
    retry: 1,
  });

  return {
    ...query,
    managers: query.data?.managers ?? [],
    pendingInvites: query.data?.pendingInvites ?? [],
  };
};

export const useInviteManager = (businessId: string | undefined) => {
  const axiosInstance = useAuthAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      const { data } = await axiosInstance.post(
        API.inviteManager(businessId!),
        { email }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Invite sent successfully");
      queryClient.invalidateQueries({
        queryKey: managersKey(businessId),
      });
    },
    onError: (e: any) =>
      toast.error(
        errorMessage(e?.response?.data?.message || e?.message)
      ),
  });
};

export const useRemoveManager = (businessId: string | undefined) => {
  const axiosInstance = useAuthAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (managerUserId: string) => {
      const { data } = await axiosInstance.delete(
        API.removeManager(businessId!, managerUserId)
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Manager removed");
      queryClient.invalidateQueries({
        queryKey: managersKey(businessId),
      });
    },
    onError: (e: any) =>
      toast.error(
        errorMessage(e?.response?.data?.message || e?.message)
      ),
  });
};
